import type { TileSet } from './tiles';
import { pairs, type WorldMapSpec } from './worldSpec';

/** Frostpeak Range's painting (approved 29 October), tiled as
 *  public/world/frost/v1 at WebP Q80. A changed painting goes in a new
 *  version folder, with its spots measured again. */
export const FROST_TILES: TileSet = {
  base: '/world/frost/v1/map_files',
  width: 3072,
  height: 2048,
  tileSize: 512,
  top: 3,
};

/**
 * Frostpeak Range, from the painting's spots.json: a snowy valley of pines and
 * a frozen lake, its seven buildings along a packed-snow road (node ids 15 to
 * 21, the world's slots in order). The Hot Springs' and the Glacier Keep's
 * outlines hug their thin structures (the bathhouse and its deck, the castle's
 * spires), so each carries a padded `tap` shape a finger can hit; the rest are
 * tapped by their outlines. The lake is ice, not water: the water mask is the
 * hot spring pools only. Every plate hangs where the painting leaves room
 * beside or above its building (Jon, 26 October: beside or above, never
 * painted for), clear of every outline, the pieces' places and the
 * neighbours' padded tap shapes. The bell house's plate hangs directly below
 * it, in the saddle between it, the lodge and the cavern: a spot beside it
 * is 400 px or more from its middle, so a phone centred on it hid the plate.
 * The lodge, the bell house, the cavern, the observatory, the hot springs and
 * the Glacier Keep take a narrow two-line plate on the smallest phone, where
 * a wide one would reach a neighbour or read as the neighbour's.
 */
export const FROST: WorldMapSpec = {
  biome: 'frost',
  tiles: FROST_TILES,
  buildings: [
    {
      node: 15,
      name: 'Frost Reels',
      centre: [330, 880],
      plate: [285, 478],
      plateTight: [290, 452],
      plateNarrow: true,
      outline: pairs([
        248, 600, 324, 600, 330, 644, 360, 654, 430, 690, 480, 770, 524, 820, 528, 900, 500, 940,
        524, 990, 490, 1030, 464, 1056, 330, 1064, 280, 1000, 210, 980, 190, 940, 150, 840, 124,
        780, 144, 716, 220, 690, 244, 660,
      ]),
    },
    {
      node: 16,
      name: 'Avalanche',
      centre: [710, 430],
      plate: [715, 640],
      plateTight: [715, 640],
      plateNarrow: true,
      outline: pairs([
        700, 80, 736, 120, 732, 180, 744, 216, 830, 252, 840, 224, 864, 230, 860, 252, 880, 300,
        912, 380, 916, 444, 904, 480, 940, 504, 944, 550, 890, 570, 860, 600, 850, 630, 770, 636,
        744, 610, 660, 600, 600, 592, 596, 552, 540, 536, 516, 500, 510, 470, 536, 436, 550, 364,
        600, 320, 670, 236, 676, 190, 670, 160, 684, 124,
      ]),
    },
    {
      node: 17,
      name: 'Ice Cavern',
      centre: [1130, 860],
      plate: [1198, 460],
      plateTight: [1257, 428],
      plateNarrow: true,
      outline: pairs([
        1146, 574, 1180, 610, 1280, 750, 1344, 860, 1380, 930, 1410, 980, 1380, 1040, 1320, 1070,
        1250, 1080, 1160, 1060, 1090, 1040, 1000, 1070, 930, 1076, 880, 1040, 850, 970, 900, 840,
        960, 740, 1040, 660, 1110, 600,
      ]),
    },
    {
      node: 18,
      name: 'Aurora',
      centre: [1720, 500],
      plate: [2090, 270],
      plateTight: [2110, 330],
      plateNarrow: true,
      outline: pairs([
        1720, 288, 1780, 284, 1840, 280, 1864, 300, 1852, 330, 1830, 390, 1840, 410, 1864, 510,
        1872, 550, 1860, 600, 1900, 570, 1906, 630, 1870, 650, 1850, 680, 1810, 712, 1740, 716,
        1660, 720, 1580, 744, 1546, 740, 1546, 650, 1570, 600, 1566, 530, 1586, 460, 1594, 410,
        1610, 360, 1650, 310, 1690, 290,
      ]),
    },
    {
      node: 19,
      name: "Yeti's Den",
      centre: [1720, 1090],
      plate: [1800, 1330],
      plateTight: [1810, 1330],
      outline: pairs([
        1680, 866, 1760, 884, 1830, 950, 1890, 1040, 1950, 1130, 1990, 1220, 1984, 1270, 1900, 1296,
        1800, 1310, 1720, 1320, 1640, 1316, 1560, 1300, 1510, 1250, 1484, 1180, 1500, 1080, 1550,
        980, 1600, 910,
      ]),
    },
    {
      node: 20,
      name: 'Hot Springs',
      centre: [2500, 1000],
      plate: [2420, 735],
      plateTight: [2428, 708],
      plateNarrow: true,
      outline: pairs([
        2434, 856, 2510, 864, 2524, 896, 2580, 994, 2584, 1024, 2616, 1076, 2616, 1116, 2592, 1150,
        2588, 1190, 2566, 1220, 2530, 1280, 2470, 1304, 2426, 1280, 2440, 1236, 2420, 1200, 2410,
        1156, 2406, 1124, 2430, 1090, 2380, 1070, 2300, 1050, 2240, 1044, 2230, 1000, 2224, 940,
        2300, 904, 2370, 910, 2400, 880,
      ]),
      tap: pairs([
        2200, 860, 2520, 844, 2620, 980, 2636, 1120, 2592, 1280, 2480, 1324, 2240, 1312, 2140, 1220,
        2120, 1040, 2150, 920,
      ]),
    },
    {
      node: 21,
      name: 'Glacier Keep',
      centre: [2880, 840],
      plate: [2526, 436],
      plateTight: [2540, 420],
      plateNarrow: true,
      outline: pairs([
        2894, 384, 2916, 444, 2940, 510, 2952, 570, 2986, 580, 3000, 640, 3020, 690, 3036, 734,
        3040, 800, 3056, 880, 3072, 940, 3072, 1070, 3020, 1106, 2960, 1124, 2910, 1130, 2850, 1104,
        2790, 1090, 2750, 1050, 2720, 1000, 2690, 970, 2672, 924, 2672, 820, 2686, 756, 2716, 680,
        2744, 530, 2780, 600, 2816, 524, 2860, 470, 2880, 420,
      ]),
      tap: pairs([
        2894, 372, 2980, 560, 3072, 680, 3072, 940, 3072, 1070, 3072, 1140, 2660, 1140, 2650, 800,
        2720, 520, 2800, 480,
      ]),
    },
  ],
  road: pairs([
    0, 1024, 100, 1054, 200, 1104, 300, 1156, 400, 1196, 500, 1206, 600, 1196, 680, 1170, 800, 1120,
    960, 1156, 1120, 1180, 1280, 1176, 1400, 1120, 1456, 1010, 1564, 904, 1680, 876, 1836, 888,
    1952, 968, 2008, 1080, 2060, 1220, 2170, 1312, 2340, 1372, 2520, 1400, 2680, 1380, 2840, 1320,
    3000, 1250, 3072, 1224,
  ]),
  roadWidth: 70,
  still: [
    {
      name: 'pool fence at the left',
      outline: pairs([
        2096, 1090, 2190, 1090, 2190, 1150, 2096, 1150,
      ]),
    },
    {
      name: 'pool fence and gate at the bottom',
      outline: pairs([
        2170, 1230, 2270, 1230, 2270, 1304, 2170, 1304,
      ]),
    },
  ],
  // The painting has no lamps of its own, and the road runs between wooden
  // fences, pines, rocks and ice, so the posts the code draws stand where each
  // was proved on open snow beside the road (a 2x crop at every foot, 30 October):
  // off the fences, trunks, rocks, ice, buildings, plates and still areas, and
  // clear of the standing pieces.
  lanternPosts: pairs([
    200, 1199, 421, 1140, 588, 1257, 694, 1091, 889, 1218, 1087, 1114, 1254, 1237, 1494, 883, 1636,
    817, 2040, 1001, 2108, 1333, 2276, 1404, 2531, 1463, 2629, 1322, 2825, 1390, 2939, 1207,
  ]),
  plateBox: [420, 110],
  plateBoxTight: [528, 138],
  plateBoxNarrow: [394, 138],
  ambient: '/world/frost/ambient/v1',
  ambientFiles: ['life.json', 'life.webp', 'sway.webp', 'water.webp'],
  // Frostpeak Range's road friends' kinds: no mittens, polar bears, penguins,
  // snow spirits or wisps, deer or reindeer, foxes in its life (mountain goats,
  // a husky sled, snow birds and the yeti at its den stand in).
  forbidden: ['mitten', 'polar', 'bear', 'penguin', 'spirit', 'wisp', 'deer', 'reindeer', 'fox'],
};
